"use client";

import Link from "next/link";
import { storyblokEditable } from "@storyblok/react/rsc";
import { GridContainer } from "@/components/layout/GridContainer";
import { JobBadge } from "@/components/layout/JobBadge";
import { ArrowRightThin } from "@/components/icons";
import type { JobBadgeStoryblok, JobListingStoryblok, JobStoryblok } from "@/component-types-sb";

type JobListingBackgroundColor = "none" | "white" | "sand-100" | "sky-50";

const backgroundColorClasses: Record<JobListingBackgroundColor, string> = {
  none: "bg-transparent",
  white: "bg-white",
  "sand-100": "bg-sand-100",
  "sky-50": "bg-sky-50",
};

type ResolvedJob = {
  content: JobStoryblok;
  full_slug: string;
  uuid: string;
};

type JobListingProps = {
  blok: JobListingStoryblok;
};

const JobListing = ({ blok }: JobListingProps) => {
  const jobs = (blok.jobs || []).filter(
    (job): boolean =>
      typeof job !== "string" && job !== null && "content" in job,
  ) as unknown as ResolvedJob[];

  const bgColor = (blok.background_color || "none") as JobListingBackgroundColor;
  const bgClass = backgroundColorClasses[bgColor] || backgroundColorClasses.none;

  return (
    <div {...storyblokEditable(blok)}>
      <div className={`${bgClass} rounded-3xl md:rounded-none`}>
      <GridContainer className="py-8 md:py-12 md:!px-0">
        <div className="col-span-12 md:col-span-10 md:col-start-2 flex flex-col gap-6 items-center">
          {/* Title */}
          {blok.title && (
            <h2 className="text-headline-xl text-black text-center">
              {blok.title}
            </h2>
          )}

          {/* Job List */}
          {jobs.length > 0 && (
            <div className="flex flex-col gap-4 w-full">
              {jobs.map((job) => (
                <Link
                  key={job.uuid}
                  href={`/${job.full_slug}`}
                  className="bg-white rounded-2xl p-4 flex items-center justify-between gap-4 cursor-pointer hover:shadow-sm transition-shadow"
                >
                  <div className="flex flex-col gap-2">
                    {/* Title */}
                    <p className="text-headline-md-static text-black">
                      {job.content.title}
                    </p>
                    {/* Badges */}
                    {job.content.badges && job.content.badges.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {job.content.badges.map((badge: JobBadgeStoryblok) => (
                          <JobBadge
                            key={badge._uid}
                            label={badge.label}
                            color={badge.color}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-subline-sm text-black-80 hidden sm:inline">
                      Zur Stellenausschreibung
                    </span>
                    <ArrowRightThin />
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Empty state */}
          {jobs.length === 0 && (
            <p className="text-copy text-center text-gray-100">
              Aktuell keine offenen Stellen
            </p>
          )}
        </div>
      </GridContainer>
      </div>
    </div>
  );
};

export default JobListing;
